import type { ReactNode, HTMLAttributes } from 'react';
import { NavItem } from '../../basic/NavItem';
import type { NavItemProps } from '../../basic/NavItem';
import styles from './Sidebar.module.css';

export interface MenuItem extends Omit<NavItemProps, 'className'> {
  icon: ReactNode;
  label: string;
}

export interface SidebarProps extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
  logo: ReactNode;
  logoHref?: string;
  menuItems: MenuItem[];
  footerItemLabels?: string[];
  footerText?: string;
  isOpen?: boolean;
  onClose?: () => void;
  className?: string;
}

export function Sidebar({
  logo,
  logoHref = '/',
  menuItems,
  footerItemLabels,
  footerText,
  isOpen = false,
  onClose,
  className,
  ...props
}: SidebarProps) {
  // Split menu items into main and footer
  // Items go to footer if they have variant="danger" OR their label is in footerItemLabels
  const footerItemLabelSet = new Set(footerItemLabels || []);
  const mainItems = menuItems.filter(
    item => item.variant !== 'danger' && !footerItemLabelSet.has(item.label)
  );
  const footerItems = menuItems.filter(
    item => item.variant === 'danger' || footerItemLabelSet.has(item.label)
  );

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className={styles.overlay}
          onClick={handleOverlayClick}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && onClose) {
              onClose();
            }
          }}
          aria-hidden="true"
          tabIndex={0} // Make the overlay focusable
          data-testid="sidebar-overlay"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`${styles.sidebar} ${isOpen ? styles.open : ''} ${className || ''}`}
        aria-label="Navegación principal"
        {...props}
      >
        {/* Header */}
        <div className={styles.header}>
          <a href={logoHref} className={styles.logo} aria-label="Ir al inicio">
            {logo}
          </a>
        </div>

        {/* Main menu */}
        <nav className={styles.mainMenu} aria-label="Menú principal">
          {mainItems.map((item, index) => (
            <NavItem key={`main-${index}`} {...item} />
          ))}
        </nav>

        {/* Footer */}
        <div className={styles.footer}>
          {footerItems.map((item, index) => (
            <NavItem key={`footer-${index}`} {...item} />
          ))}
          {footerText && (
            <p className={styles.footerText}>{footerText}</p>
          )}
        </div>
      </aside>
    </>
  );
}

Sidebar.displayName = 'Sidebar';
